import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useManualQuery, useMutation } from 'graphql-hooks';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';

import { CommentRow } from '../CommentRow';
import { CustomInput } from '../CustomInput';
import { MentionSuggestions } from '../MentionSuggestions';
import { COMMENTS_PAGINATION, GUEST_COMMENTS_PAGINATION } from '../../graphql/comment/query';
import { ADD_COMMENT } from '../../graphql/comment/mutation';
import { LIKE_COMMENT, UNLIKE_COMMENT } from '../../graphql/like/mutation';
import { AuthReason } from '../../atoms';
import {
  getErrorMessage,
  getMentionIds,
  getMentionToken,
} from '../../helpers';
import useMentionSuggestions from '../../hooks/useMentionSuggestions';
import usePageMessage from '../../hooks/usePageMessage';
import {
  Comment,
  CommentType,
  User,
  Video,
} from '../../types';
import { commentsReducer, initialState } from './commentsReducer';
import type { MentionGroup } from './commentsReducer';
import styles from './style';

const LIMIT = 10;
const LOAD_MORE_RATIO = 0.7;
// A comment opened from a notification stays highlighted this long.
const HIGHLIGHT_DURATION = 2000;

interface CommentsPanelProps {
  video: Video;
  onClose?: () => void;
  // A comment was added: the counter of the video changes.
  onCommented: () => void;
  // A visitor reads the comments: writing, liking or opening a profile asks to log in.
  onLocked?: (reason: AuthReason) => void;
  // Comment opened from a notification: scrolled to and highlighted for a moment.
  highlightId?: string;
  // Inside another panel (ranking): no card of its own, no title nor close button.
  embedded?: boolean;
}

const CommentsPanel: React.FC<CommentsPanelProps> = React.memo((props) => {
  const {
    video,
    onClose,
    onCommented,
    onLocked,
    highlightId,
    embedded,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [commentsPagination] = useManualQuery(onLocked ? GUEST_COMMENTS_PAGINATION : COMMENTS_PAGINATION, { skipCache: true });
  const [addComment] = useMutation(ADD_COMMENT);
  const { showMessage } = usePageMessage();
  const [likeComment] = useMutation(LIKE_COMMENT);
  const [unLikeComment] = useMutation(UNLIKE_COMMENT);
  // The whole state of the panel (list, reply, draft, mentions, highlight) lives in the reducer:
  // its pieces change together (a sent comment clears the draft, opens the parent...).
  const [state, dispatch] = useReducer(commentsReducer, initialState);
  const {
    comments,
    hasNextPage,
    loading,
    expanded,
    replyTo,
    message,
    sending,
    mentionToken,
    mentionGroup,
    activeSuggestion,
    pickedUsers,
    highlighted,
  } = state;
  const {
    suggestions,
    loading: loadingSuggestions,
    search: searchMentions,
    loadMore: loadMoreMentions,
    clear: clearMentions,
  } = useMentionSuggestions();
  const loadingRef = useRef(false);
  // Another video asked meanwhile: the answer of the previous one is ignored.
  const requestIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Caret to restore after a mention is inserted.
  const caretRef = useRef<number | null>(null);

  const getPage = useCallback(async (offset: number) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    loadingRef.current = true;
    dispatch({ type: 'pageRequested' });
    const { data } = await commentsPagination({
      variables: { input: { videoId: video.id, offset, limit: LIMIT } },
    }) as any;
    if (requestId !== requestIdRef.current) {
      return;
    }
    const page = data && (data.commentsPagination || data.guestCommentsPagination);
    loadingRef.current = false;
    if (page) {
      const { docs, hasNextPage: next } = page;
      dispatch({
        type: 'commentsLoaded',
        docs,
        hasNextPage: Boolean(next),
        offset,
      });
    } else {
      dispatch({ type: 'loadingEnded' });
    }
  }, [commentsPagination, video.id]);

  // Another video: its own comments, no reply in progress.
  useEffect(() => {
    dispatch({ type: 'videoChanged' });
    clearMentions();
    getPage(0);
  }, [clearMentions, getPage]);

  // The comment of the notification is scrolled into view once loaded, and highlighted 2s.
  useEffect(() => {
    if (!highlightId || loading || !comments.length) {
      return undefined;
    }
    const parent = comments.find((c) => c.id === highlightId || (c.comments || []).some((r) => r.id === highlightId));
    if (!parent) {
      return undefined;
    }
    dispatch({ type: 'highlight', commentId: highlightId, parentId: parent.id });
    const timer = setTimeout(() => dispatch({ type: 'highlightEnded' }), HIGHLIGHT_DURATION);
    return () => clearTimeout(timer);
    // Only when the list of the video arrives, not on each like.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, loading, comments.length]);

  useEffect(() => {
    if (!highlighted || !listRef.current) {
      return;
    }
    const node = listRef.current.querySelector(`[data-comment-id="${highlighted}"]`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'center' });
    }
  }, [highlighted]);

  // The caret goes after the inserted mention once the field shows the new text.
  useEffect(() => {
    if (caretRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  }, [message]);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (hasNextPage && !loadingRef.current && scrollTop + clientHeight >= scrollHeight * LOAD_MORE_RATIO) {
      getPage(comments.length);
    }
  }, [comments.length, getPage, hasNextPage]);

  const onLike = useCallback(async (comment: Comment) => {
    if (onLocked) {
      onLocked('commentLike');
      return;
    }
    dispatch({ type: 'likeToggled', commentId: comment.id });
    const mutation = comment.isLiked ? unLikeComment : likeComment;
    const { error } = await mutation({ variables: { id: comment.id } });
    if (error) {
      dispatch({ type: 'likeToggled', commentId: comment.id });
    }
  }, [likeComment, onLocked, unLikeComment]);

  // A reply always goes under the parent comment, to the user who wrote the answered comment.
  const onReply = useCallback((comment: Comment, parent?: Comment) => {
    if (onLocked) {
      onLocked('comment');
      return;
    }
    const username = comment.user ? comment.user.username : '';
    dispatch({
      type: 'replyTo',
      parent: parent || comment,
      receiverId: comment.userId,
      username,
    });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onLocked]);

  const cancelReply = useCallback(() => {
    dispatch({ type: 'cancelReply' });
  }, []);

  const onOpenUser = useCallback((userId: string) => {
    if (onLocked) {
      onLocked('profile');
      return;
    }
    navigate(`/u/${userId}`);
  }, [navigate, onLocked]);

  const askComment = useCallback(() => {
    if (onLocked) {
      onLocked('comment');
    }
  }, [onLocked]);

  const toggleReplies = useCallback((parentId: string) => {
    dispatch({ type: 'repliesToggled', parentId });
  }, []);

  // Looks for an @ at the caret: the suggestions follow what is typed after it.
  const updateMentionToken = useCallback((text: string, caret: number) => {
    const token = getMentionToken(text, caret);
    dispatch({ type: 'mentionTokenChanged', token });
    if (token) {
      searchMentions(token.query);
    } else {
      clearMentions();
    }
  }, [clearMentions, searchMentions]);

  const onChangeMessage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.target;
    dispatch({ type: 'textChanged', text: value });
    updateMentionToken(value, selectionStart === null ? value.length : selectionStart);
  }, [updateMentionToken]);

  // The caret moved (click, arrows): the @ under it, if any, is the one suggested.
  const onSelectMessage = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.currentTarget;
    // Ignored until the caret is put after a checked user.
    if (document.activeElement !== event.currentTarget || caretRef.current !== null) {
      return;
    }
    const caret = selectionStart === null ? value.length : selectionStart;
    // The caret stays in the "@a @b" being checked: the list stays open.
    if (mentionGroup && caret >= mentionGroup.start && caret <= mentionGroup.end) {
      return;
    }
    const token = getMentionToken(value, caret);
    if ((token && token.query) !== (mentionToken && mentionToken.query) || !!token !== !!mentionToken) {
      updateMentionToken(value, caret);
    }
  }, [mentionGroup, mentionToken, updateMentionToken]);

  const closeMentions = useCallback(() => {
    dispatch({ type: 'mentionsClosed' });
    clearMentions();
  }, [clearMentions]);

  // Like the app: a click checks or unchecks a user, "@a @b" is written and the list stays open.
  const pickMention = useCallback((user: User) => {
    const range = mentionGroup || mentionToken;
    if (!range) {
      return;
    }
    const prev = mentionGroup ? mentionGroup.usernames : [];
    const name = user.username.toLowerCase();
    const usernames = prev.some((u) => u.toLowerCase() === name)
      ? prev.filter((u) => u.toLowerCase() !== name)
      : [...prev, user.username];
    const written = usernames.length ? `@${usernames.join(' @')}` : '';
    const text = `${message.slice(0, range.start)}${written}${message.slice(range.end)}`;
    const end = range.start + written.length;
    // No user left: the @ is removed and the list closes.
    const group: MentionGroup | null = usernames.length
      ? { start: range.start, end, query: mentionGroup ? mentionGroup.query : range.query, usernames }
      : null;
    caretRef.current = end;
    dispatch({ type: 'mentionToggled', text, user, group });
    if (!group) {
      clearMentions();
    }
  }, [clearMentions, mentionGroup, mentionToken, message]);

  const onHoverSuggestion = useCallback((index: number) => {
    dispatch({ type: 'activeSuggestionChanged', index });
  }, []);

  const send = useCallback(async () => {
    const text = message.trim();
    if (!text || sending) {
      return;
    }
    dispatch({ type: 'sendStarted' });
    const mentionedUserIds = getMentionIds(text, pickedUsers);
    const input = replyTo
      ? { message: text, type: CommentType.CHILD, videoId: video.id, commentId: replyTo.parent.id, receiverId: replyTo.receiverId, mentionedUserIds }
      : { message: text, type: CommentType.PARENT, videoId: video.id, receiverId: video.userId, mentionedUserIds };
    const { data, error } = await addComment({ variables: { input } }) as any;
    if (!data || !data.addComment) {
      dispatch({ type: 'sendFailed' });
      // The server refuses the comments of the test account, like in the app.
      if (getErrorMessage(error, '') === 'COMMENT_DISABLED') {
        showMessage(t('admin.feed.commentDisabled'), 'warning');
      }
      return;
    }
    const added = { ...data.addComment, comments: [], isLiked: false } as Comment;
    dispatch({ type: 'commentAdded', comment: added, parentId: replyTo ? replyTo.parent.id : '' });
    clearMentions();
    onCommented();
  }, [addComment, clearMentions, message, onCommented, pickedUsers, replyTo, sending, showMessage, t, video.id, video.userId]);

  // The wheel scrolls the comments, it must not change the video of the feed.
  const stopWheel = useCallback((event: React.WheelEvent) => {
    event.stopPropagation();
  }, []);

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    // The arrows move in the text, they must not change the video of the feed.
    event.stopPropagation();
    if (mentionToken || mentionGroup) {
      // The suggestions take the arrows, Enter and Escape while they are open.
      if (event.key === 'ArrowDown' && suggestions.length) {
        event.preventDefault();
        dispatch({ type: 'activeSuggestionMoved', delta: 1, count: suggestions.length });
        return;
      }
      if (event.key === 'ArrowUp' && suggestions.length) {
        event.preventDefault();
        dispatch({ type: 'activeSuggestionMoved', delta: -1, count: suggestions.length });
        return;
      }
      if (event.key === 'Enter' && suggestions.length) {
        event.preventDefault();
        pickMention(suggestions[activeSuggestion] || suggestions[0]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMentions();
        return;
      }
    }
    if (event.key === 'Enter') {
      send();
    }
  }, [activeSuggestion, closeMentions, mentionGroup, mentionToken, pickMention, send, suggestions]);

  const isEmpty = !loading && !comments.length;

  return (
    <Box component="aside" sx={embedded ? styles.embedded : styles.panel}>
      {!embedded && (
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{t('admin.feed.commentsTitle', { count: video.comments || 0 })}</Typography>
          <IconButton onClick={onClose} aria-label={t('admin.common.close')} sx={styles.close}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Box ref={listRef} sx={styles.list} onScroll={onScroll} onWheel={stopWheel}>
        {isEmpty && <Typography sx={styles.empty}>{t('admin.feed.noComments')}</Typography>}
        {comments.map((comment) => {
          const replies = comment.comments || [];
          const isOpen = expanded.includes(comment.id);
          return (
            <Box key={comment.id}>
              <CommentRow
                comment={comment}
                highlighted={highlighted === comment.id}
                onLike={onLike}
                onReply={onReply}
                onOpenUser={onOpenUser}
              />
              {isOpen && replies.map((reply) => (
                <CommentRow
                  key={reply.id}
                  comment={reply}
                  parent={comment}
                  highlighted={highlighted === reply.id}
                  onLike={onLike}
                  onReply={onReply}
                  onOpenUser={onOpenUser}
                />
              ))}
              {replies.length > 0 && (
                <Box component="button" type="button" onClick={() => toggleReplies(comment.id)} sx={styles.toggleReplies}>
                  {isOpen ? t('admin.feed.hideReplies') : t('admin.feed.showReplies', { count: replies.length })}
                </Box>
              )}
            </Box>
          );
        })}
        {loading && (
          <Box sx={styles.loading}>
            <CircularProgress size={16} sx={styles.spinner} />
          </Box>
        )}
      </Box>
      {replyTo && (
        <Box sx={styles.replyBar}>
          {t('admin.feed.replyPlaceholder', { username: replyTo.username })}
          <Box component="button" type="button" onClick={cancelReply} sx={styles.cancelReply}>{t('admin.feed.cancelReply')}</Box>
        </Box>
      )}
      {onLocked && (
        <Box sx={styles.guestForm}>
          {t('admin.guest.commentInvite')}
          <Button onClick={askComment} sx={styles.send}>{t('admin.guest.signIn')}</Button>
        </Box>
      )}
      {!onLocked && (
        <Box sx={styles.form} onWheel={stopWheel}>
          {(mentionGroup || mentionToken) && (
            <MentionSuggestions
              query={mentionGroup ? mentionGroup.query : (mentionToken ? mentionToken.query : '')}
              users={suggestions}
              loading={loadingSuggestions}
              activeIndex={activeSuggestion}
              checked={mentionGroup ? mentionGroup.usernames : []}
              onPick={pickMention}
              onHover={onHoverSuggestion}
              onEndReached={loadMoreMentions}
            />
          )}
          <CustomInput
            inputRef={inputRef}
            value={message}
            onChange={onChangeMessage}
            onSelect={onSelectMessage}
            onBlur={closeMentions}
            onKeyDown={onKeyDown}
            placeholder={replyTo ? t('admin.feed.replyPlaceholder', { username: replyTo.username }) : t('admin.feed.commentPlaceholder')}
            sx={styles.input}
          />
          <Button onClick={send} disabled={!message.trim() || sending} sx={styles.send}>{t('admin.feed.send')}</Button>
        </Box>
      )}
    </Box>
  );
});

export default CommentsPanel;
